"use client";

import { useEffect, useMemo } from "react";
import { ScrollArea } from "~/components/ui/scroll-area";
import type { Changelog } from "~/data/changelog-schema";
import useChangelogStore from "~/stores/changelog";
import { ChangelogTimeline } from "./changelog-timeline";
import { VersionHeader } from "./version-header";
import { VersionRail } from "./version-rail";

export const ChangelogViewer = ({ changelog }: { changelog: Changelog }) => {
	const selectedVersion = useChangelogStore((state) => state.selectedVersion);
	const setSelectedVersion = useChangelogStore(
		(state) => state.setSelectedVersion
	);

	const { versions } = changelog;
	const active = useMemo(
		() =>
			versions.find((version) => version.version === selectedVersion) ??
			versions[0],
		[versions, selectedVersion]
	);

	useEffect(() => {
		const fallback = changelog.currentVersion ?? versions[0]?.version ?? null;
		const exists = versions.some(
			(version) => version.version === selectedVersion
		);
		if (!exists && fallback) {
			setSelectedVersion(fallback);
		}
	}, [selectedVersion, versions, changelog.currentVersion, setSelectedVersion]);

	if (!active) {
		return (
			<div className="flex flex-1 items-center justify-center p-10 text-center text-muted-foreground text-sm">
				No changelog entries have been published yet.
			</div>
		);
	}

	return (
		<div className="grid min-h-0 flex-1 grid-cols-1 sm:grid-cols-[210px_1fr]">
			<div className="flex min-h-0 flex-col border-border/50 border-b bg-muted/20 max-sm:max-h-44 sm:border-r sm:border-b-0">
				<ScrollArea className="flex-1 px-1.5 py-3">
					<VersionRail
						currentVersion={changelog.currentVersion}
						onSelect={setSelectedVersion}
						selected={active.version}
						versions={versions}
					/>
				</ScrollArea>
			</div>
			<ScrollArea className="min-h-0 flex-1">
				<div className="px-6 py-5">
					<VersionHeader version={active} />
					<ChangelogTimeline version={active} />
				</div>
			</ScrollArea>
		</div>
	);
};
